/**
 * SCRUM-1032 — "which rows have I already changed?" for the guided decoder
 * lists.
 *
 * A controller can carry hundreds of decoders and the setup is entered over
 * several sittings. The server answers "is this decoder configured"
 * (`configState`), which is a property of the data, not a record of the user's
 * own work: a row that arrived configured is indistinguishable from one they
 * set up themselves, so on the next visit they redo it.
 *
 * This records the missing half locally: which rows the user has actually
 * changed. Nothing to click — a row is marked because an edit was saved for
 * it. It is per browser by design: no API field exists for it (a server-side
 * one is worth its own ticket). Everything here degrades to "nothing edited
 * yet", never to a wrong answer.
 */
export interface GuidedDecoderProgress {
  version: 2
  /** Row keys (`address-index`) the user has edited on this controller. */
  editedRowKeys: string[]
  updatedAt: string
}

const storageKey = (interfaceId: number, tab: string) =>
  `guided-decoders:v1:${interfaceId}:${tab}`

export const loadGuidedDecoderProgress = (
  interfaceId: number,
  tab: string,
): GuidedDecoderProgress | null => {
  if (typeof window === 'undefined' || !Number.isFinite(interfaceId)) return null

  try {
    const raw = window.localStorage.getItem(storageKey(interfaceId, tab))
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<GuidedDecoderProgress>
    // A v1 blob held hand-ticked rows, which no longer mean the same thing.
    if (
      parsed.version !== 2 ||
      !Array.isArray(parsed.editedRowKeys) ||
      !parsed.editedRowKeys.every((key) => typeof key === 'string')
    ) {
      window.localStorage.removeItem(storageKey(interfaceId, tab))
      return null
    }

    return parsed as GuidedDecoderProgress
  } catch {
    return null
  }
}

export const saveGuidedDecoderProgress = (
  interfaceId: number,
  tab: string,
  progress: Omit<GuidedDecoderProgress, 'version' | 'updatedAt'>,
) => {
  if (typeof window === 'undefined' || !Number.isFinite(interfaceId)) return

  try {
    window.localStorage.setItem(
      storageKey(interfaceId, tab),
      JSON.stringify({
        version: 2,
        ...progress,
        updatedAt: new Date().toISOString(),
      } satisfies GuidedDecoderProgress),
    )
  } catch {
    // A full or blocked storage costs the resume marker, not the session.
  }
}

export const clearGuidedDecoderProgress = (interfaceId: number, tab: string) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(storageKey(interfaceId, tab))
  } catch {
    // Nothing to do — the caller only ever wanted it gone.
  }
}
